import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Eye, EyeOff } from "lucide-react";

const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm(""); setError(""); setShowPw(false);
  };

  const handleOpenChange = (v) => { if (v) reset(); onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (next !== confirm) { setError("New password and confirmation do not match."); return; }
    if (current === next) { setError("New password must be different from current."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Use your new password next time you sign in.");
      handleOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not update password");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold flex items-center gap-2">
            <KeyRound className="w-3 h-3" /> Account Security
          </div>
          <DialogTitle className="font-display text-2xl">Change password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="text-sm">Current password</Label>
            <div className="relative">
              <Input
                data-testid="cp-current"
                type={showPw ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="h-11 rounded-lg pr-10"
                required
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B5944]">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm">New password</Label>
            <Input
              data-testid="cp-new"
              type={showPw ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={6}
              className="h-11 rounded-lg"
              required
              placeholder="At least 6 characters"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Confirm new password</Label>
            <Input
              data-testid="cp-confirm"
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={6}
              className="h-11 rounded-lg"
              required
            />
          </div>
          {error && <div className="text-sm text-[#A23E3E] bg-[#A23E3E]/10 rounded-lg p-2.5" data-testid="cp-error">{error}</div>}
          <div className="text-xs text-[#6B5944] bg-[#FDFBF5] p-3 rounded-lg border border-[#EBE3D2]">
            💡 Tip: pick something memorable to you but not obvious — at least 8 characters, mixing letters, numbers, and a symbol.
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="cp-submit">
              {saving ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
